import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale, useT, withLocale } from '../i18n';
import { contentApi, type Solution, type Industry } from '../api/content';

const COMPANY_LINKS: Array<[string, string]> = [
  ['footer.platform', '/platform'],
  ['footer.ai_lab', '/ai-lab'],
  ['footer.methodology', '/methodology'],
  ['footer.national', '/national-transformation'],
  ['footer.academy', '/about#academy'],
  ['footer.insights', '/insights'],
  ['footer.about', '/about'],
];

const TRUST_LINKS: Array<[string, string]> = [
  ['footer.security', '/security'],
  ['footer.sovereignty', '/sovereignty'],
  ['footer.team', '/team'],
  ['footer.portal', '/portal-login'],
  ['footer.legal', '/legal'],
];

const LEGAL_ANCHORS: Array<[string, string]> = [
  ['footer.legal_notice', '#notice'],
  ['footer.privacy', '#privacy'],
  ['footer.cookies', '#cookies'],
  ['footer.terms_use', '#terms-use'],
  ['footer.terms_service', '#terms-service'],
  ['footer.dpa', '#dpa'],
];

export default function Footer() {
  const locale = useLocale();
  const t = useT();
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    contentApi.solutions(locale).then(setSolutions).catch(() => setSolutions([]));
    contentApi.industries(locale).then((all) => setIndustries(all.slice(0, 6))).catch(() => setIndustries([]));
  }, [locale]);

  return (
    <footer className="site-footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-col">
            <Link to={withLocale('/', locale)} className="brand" style={{ marginBottom: 16 }}>
              <img src="/assets/img/cyrkil-mark.png" alt="CYRKIL" className="brand-mark-img" style={{ width: 30, height: 30 }} />
              <span>CYRKIL</span>
            </Link>
            <p className="body-sm">{t('footer.tagline')}</p>
            <form
              className="footer-newsletter"
              onSubmit={(e) => {
                e.preventDefault();
                setSubscribed(true);
              }}
            >
              <input type="email" placeholder={t('common.email_placeholder')} aria-label={t('common.email_placeholder')} value={email} onChange={(e) => setEmail(e.target.value)} required />
              <button className="btn btn-secondary btn-sm" type="submit">
                {subscribed ? '✓' : t('common.subscribe')}
              </button>
            </form>
            <p className="body-sm" style={{ marginTop: 8, fontSize: '.72rem' }}>
              {t('common.newsletter_note')}
            </p>
          </div>

          <div className="footer-col">
            <h4>{t('footer.solutions')}</h4>
            {solutions.map((s) => (
              <Link key={s.code} to={withLocale(`/solutions/${s.slug}`, locale)}>
                {s.name}
              </Link>
            ))}
            <Link to={withLocale('/solutions', locale)}>{t('nav.view_all_12')}</Link>
          </div>

          <div className="footer-col">
            <h4>{t('footer.industries')}</h4>
            {industries.map((i) => (
              <Link key={i.slug} to={withLocale(`/industries/${i.slug}`, locale)}>
                {i.name}
              </Link>
            ))}
            <Link to={withLocale('/industries', locale)}>{t('nav.view_all_10')}</Link>
          </div>

          <div className="footer-col">
            <h4>{t('footer.company')}</h4>
            {COMPANY_LINKS.map(([key, href]) => (
              <Link key={key} to={withLocale(href, locale)}>
                {t(key)}
              </Link>
            ))}
          </div>

          <div className="footer-col">
            <h4>{t('footer.trust_legal')}</h4>
            {TRUST_LINKS.map(([key, href]) => (
              <Link key={key} to={withLocale(href, locale)}>
                {t(key)}
              </Link>
            ))}
          </div>
        </div>

        <div className="footer-legal">
          <div>
            CYRKIL SASU — Société Française d'Intelligence Artificielle et de Systèmes Intelligents · Share capital €10,000
            <br />
            102 avenue des Champs-Élysées, 75008 Paris · RCS Paris 993 473 602 · FR7501993473602 · <a href="mailto:ceo@cyrkil.fr">ceo@cyrkil.fr</a>
          </div>
          <div className="footer-legal-links">
            {LEGAL_ANCHORS.map(([key, anchor]) => (
              <Link key={key} to={withLocale('/legal', locale) + anchor}>
                {t(key)}
              </Link>
            ))}
          </div>
        </div>
        <p className="body-sm" style={{ marginTop: 16 }}>
          © {new Date().getFullYear()} CYRKIL SASU. {t('common.all_rights')}
        </p>
      </div>
    </footer>
  );
}
